/**
 * appointmentStageRouting.js
 *
 * Pure utilities for mapping appointment route paths to assessment stages and
 * managing the preview query parameter. Used by the Patient Overview to decide
 * whether a clinician can start an appointment (stage assignment check) and to
 * build the read-only preview URL for any module card.
 *
 * These functions are intentionally generic — they cover both Adult Autism and
 * ADHD route patterns so a future ADHD overview port can reuse them directly.
 */

import { isClinicianAssignedToStage } from './clinicianPermissions';

export const PREVIEW_QUERY_PARAM = 'preview';

/**
 * Appends `?preview=true` to a path, preserving any existing query parameters.
 * Handles paths that already contain a query string gracefully.
 *
 * @param {string} path - Route path (e.g. '/patient/abc/adult-autism/history')
 * @returns {string} Path with preview=true appended
 */
export function appendPreviewQuery(path) {
  if (!path) return path;

  const [pathname, search] = path.split('?');
  const params = new URLSearchParams(search || '');
  params.set(PREVIEW_QUERY_PARAM, 'true');
  return `${pathname}?${params.toString()}`;
}

/**
 * Resolves which assessment stage (CI, SSI, DD, PAD) a given route path belongs
 * to. Returns null for unrecognised paths — callers should treat null as
 * "cannot determine permission".
 *
 * Pattern matching is intentionally broad (includes-based) so it works with
 * patient-id-prefixed paths like `/patient/:id/proforma/clinical-interview/...`.
 *
 * @param {string} path - Route path
 * @returns {'CI'|'SSI'|'DD'|'PAD'|null}
 */
export function resolveStageFromAppointmentPath(path) {
  if (!path) return null;

  // Adult Autism History → Clinical Interview stage
  if (path.includes('/adult-autism/history')) return 'CI';

  // Adult ASD Semi-Structured Interview
  if (path.includes('/proforma/adult-asd/semi-structured-interview')) return 'SSI';

  // CYP ADHD routes (under /proforma/cyp-adhd/). Checked before the generic
  // adult ADHD patterns because the generic '/proforma/clinical-interview'
  // is NOT a substring of '/proforma/cyp-adhd/clinical-interview' (the
  // cyp-adhd/ segment sits in between), so these need explicit clauses.
  if (path.includes('/proforma/cyp-adhd/clinical-interview')) return 'CI';
  if (path.includes('/proforma/cyp-adhd/semi-structured-interview')) return 'SSI';
  if (path.includes('/proforma/cyp-adhd/diagnostic-decision')) return 'DD';
  if (path.includes('/proforma/cyp-adhd/post-assessment-discussion')) return 'PAD';

  // ADHD Clinical Interview
  if (path.includes('/proforma/clinical-interview')) return 'CI';

  // ADHD Semi-Structured Interview
  if (path.includes('/proforma/semi-structured-interview')) return 'SSI';

  // Adult Autism Diagnostic Decision (modular path). This MUST be checked before
  // the generic '/proforma/diagnostic-decision' clause below: the autism DD path
  // is '/proforma/adult-asd/diagnostic-decision', which does NOT contain the
  // generic substring, so without this explicit clause canStartAppointment would
  // fail to resolve the stage and deny the assigned DD clinician access.
  if (path.includes('/proforma/adult-asd/diagnostic-decision')) return 'DD';

  // Diagnostic Decision
  if (path.includes('/proforma/diagnostic-decision')) return 'DD';

  // Adult Autism Post-Assessment Discussion (modular path). Like the autism DD
  // path above, this MUST be checked before the generic clause: the autism PAD
  // path is '/proforma/adult-asd/post-assessment-discussion', which does NOT
  // contain the generic substring, so without this explicit clause
  // canStartAppointment would fail to resolve the stage and deny the assigned
  // PAD clinician access.
  if (path.includes('/proforma/adult-asd/post-assessment-discussion')) return 'PAD';

  // Post-Assessment Discussion
  if (path.includes('/proforma/post-assessment-discussion')) return 'PAD';

  // Behavioural Observation (autism only)
  if (path.includes('/proforma/adult-asd/observation')) return 'OBS';
  if (path.includes('/proforma/behavioural-observation')) return 'OBS';
  if (path.includes('/adult-autism/observation')) return 'OBS';

  return null;
}

/**
 * Determines whether a user can start/continue an appointment by resolving the
 * route path to a stage and checking their assignment via isClinicianAssignedToStage.
 *
 * @param {Object|null} patientData - Patient data model with `clinicians` field
 * @param {string|null} userId - Current user's UID
 * @param {string|null} actionPath - The route path for the Start/Continue/Review action
 * @returns {boolean}
 */
export function canStartAppointment(patientData, userId, actionPath) {
  if (!patientData || !userId || !actionPath) return false;

  const stage = resolveStageFromAppointmentPath(actionPath);
  if (!stage) return false;

  return isClinicianAssignedToStage(patientData, userId, stage);
}
